import {
  Box,
  Card,
  Stack,
  Table,
  Divider,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  IconButton,
  TableContainer,
} from '@mui/material';

import { getCurrencySymbol } from 'src/utils/currency';

import { Iconify } from 'src/components/iconify';

import { PaymentMethodEnum } from './order-new-edit-form';

// Define a type for order items
interface OrderItem {
  mediaName: string;
  price: number;
  currency: number; // ExpenseCurrencyEnum
  finalUrl: string;
  wordCount: number;
  costPrice?: number; // Precio de costo
  costPriceCurrency?: number; // Moneda del precio de costo (ExpenseCurrencyEnum)
  redactionPrice?: number; // Precio de redacción
  categoryCommission?: number; // Comisión de categoría
  discountPercentage?: number; // % de descuento aplicado
}

interface OrderBillingProps {
  orderItems: OrderItem[];
  paymentMethod: PaymentMethodEnum;
  wordCount?: number;
  onRemoveItem?: (itemId: OrderItem) => void;
  billingCountry?: string;
}

export default function OrderBilling({
  orderItems,
  paymentMethod,
  onRemoveItem,
  billingCountry = '',
}: OrderBillingProps) {
  // Calculate subtotal from order items (aplicando descuento)
  const calculateSubtotal = () =>
    orderItems.reduce((sum, item) => {
      const discountPercentage = item.discountPercentage || 0;
      const priceAfterDiscount = item.price * (1 - discountPercentage / 100);
      return sum + priceAfterDiscount;
    }, 0);

  // Calcular el subtotal visual (incluyendo descuento y comisiones de categoría)
  const calculateVisualSubtotal = () =>
    orderItems.reduce((sum, item) => {
      const discountPercentage = item.discountPercentage || 0;
      const priceAfterDiscount = item.price * (1 - discountPercentage / 100);
      const categoryCommission = item.categoryCommission || 0;
      return sum + priceAfterDiscount + categoryCommission;
    }, 0);

  // Aplicar IGI del 4.5% si el país es Andorra
  const isAndorra = billingCountry?.toLowerCase() === 'andorra';

  // Calcular IGI (4.5%) para Andorra, o 0 para otros países
  // Para el IGI visual usamos el subtotal visual
  const calculateIGI = () => (isAndorra ? calculateSubtotal() * 0.045 : 0);
  const calculateVisualIGI = () => (isAndorra ? calculateVisualSubtotal() * 0.045 : 0);

  // Calcular beneficio total (aplicando descuento)
  const calculateTotalProfit = () =>
    orderItems.reduce((sum, item) => {
      const discountPercentage = item.discountPercentage || 0;
      const priceAfterDiscount = item.price * (1 - discountPercentage / 100);
      const costPrice = item.costPrice || 0;
      const redactionPrice = item.redactionPrice || 0;
      const categoryCommission = item.categoryCommission || 0;
      const profit = priceAfterDiscount + categoryCommission - costPrice - redactionPrice;
      return sum + profit;
    }, 0);

  // El total incluye IGI si aplica
  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const igi = calculateIGI();
    return subtotal + igi;
  };

  // Total visual incluye comisiones de categoría e IGI
  const calculateVisualTotal = () => {
    const visualSubtotal = calculateVisualSubtotal();
    const visualIgi = calculateVisualIGI();
    return visualSubtotal + visualIgi;
  };

  // Get the currency from the first order item (assuming all items use same currency)
  const getCurrency = () => (orderItems.length > 0 ? orderItems[0].currency : 0); // 0 = EUR

  const subtotal = calculateSubtotal();
  const visualSubtotal = calculateVisualSubtotal();
  const visualIgi = calculateVisualIGI();
  const total = calculateTotal();
  const visualTotal = calculateVisualTotal();
  const totalProfit = calculateTotalProfit();
  const isTotalProfitPositive = totalProfit >= 0;
  const currency = getCurrency();

  // Helper function to render payment method icon and text
  const renderPaymentMethodInfo = () => {
    switch (paymentMethod) {
      case PaymentMethodEnum.PayPal:
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify icon="solar:flag-bold" width={24} />
            <Typography variant="body2">PayPal</Typography>
          </Stack>
        );
      case PaymentMethodEnum.WiseTransfer:
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify icon="solar:cart-3-bold" width={24} />
            <Typography variant="body2">Wise/Transferencia</Typography>
          </Stack>
        );
      case PaymentMethodEnum.Pending:
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify icon="solar:clock-circle-bold" width={24} />
            <Typography variant="body2">Pendiente de pago</Typography>
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Facturación
      </Typography>

      {orderItems.length === 0 ? (
        <Typography color="text.secondary">
          Añade items al pedido para ver el resumen de facturación
        </Typography>
      ) : (
        <>
          {/* Order Items Summary */}
          <TableContainer sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Medio</TableCell>
                  <TableCell align="right">Precio Base</TableCell>
                  <TableCell align="right">Descuento</TableCell>
                  <TableCell align="right">Comisión Cat.</TableCell>
                  <TableCell align="right">Precio Coste</TableCell>
                  <TableCell align="right">Total Visual</TableCell>
                  <TableCell align="right">Beneficio</TableCell>
                  {onRemoveItem && <TableCell align="right">Acciones</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {orderItems.map((item, index) => {
                  const discountPercentage = item.discountPercentage || 0;
                  const priceAfterDiscount = item.price * (1 - discountPercentage / 100);
                  const discountAmount = item.price - priceAfterDiscount;

                  // Calcular beneficio = (precio con descuento + comisión categoría) - precio costo - precio redacción
                  const costPrice = item.costPrice || 0;
                  const redactionPrice = item.redactionPrice || 0;
                  const categoryCommission = item.categoryCommission || 0;
                  const profit =
                    priceAfterDiscount + categoryCommission - costPrice - redactionPrice;
                  const isProfitPositive = profit >= 0;

                  // Total visual: precio con descuento + comisión categoría
                  const visualTotals = priceAfterDiscount + categoryCommission;

                  return (
                    <TableRow key={index}>
                      <TableCell>{item.mediaName}</TableCell>
                      <TableCell align="right">
                        {getCurrencySymbol(item.currency)}
                        {item.price.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {discountPercentage > 0 ? (
                          <Typography variant="body2" sx={{ color: 'success.main' }}>
                            -{discountPercentage}% (-{getCurrencySymbol(item.currency)}
                            {discountAmount.toFixed(2)})
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {categoryCommission > 0
                          ? `${getCurrencySymbol(item.currency)}${categoryCommission.toFixed(2)}`
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                          {getCurrencySymbol(item.costPriceCurrency)}
                          {costPrice.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {getCurrencySymbol(item.currency)}
                          {visualTotals.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            color: isProfitPositive ? 'success.main' : 'error.main',
                            fontWeight: 'bold',
                          }}
                        >
                          {getCurrencySymbol(item.currency)}
                          {profit.toFixed(2)}
                        </Typography>
                      </TableCell>
                      {onRemoveItem && (
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => onRemoveItem(item)}>
                            <Iconify icon="solar:trash-bin-trash-bold" width={20} />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Payment Method Display */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Método de pago
            </Typography>
            {renderPaymentMethodInfo()}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Calculations Summary */}
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Subtotal (sin descuentos ni comisiones)
              </Typography>
              <Typography variant="subtitle2">
                {getCurrencySymbol(currency)}
                {orderItems.reduce((sum, item) => sum + item.price, 0).toFixed(2)}
              </Typography>
            </Stack>

            {/* Total de descuentos */}
            {orderItems.some((item) => (item.discountPercentage || 0) > 0) && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  Descuentos totales
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'success.main' }}>
                  -{getCurrencySymbol(currency)}
                  {orderItems
                    .reduce((sum, item) => {
                      const discountPercentage = item.discountPercentage || 0;
                      const discountAmount = item.price * (discountPercentage / 100);
                      return sum + discountAmount;
                    }, 0)
                    .toFixed(2)}
                </Typography>
              </Stack>
            )}

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Subtotal con descuentos
              </Typography>
              <Typography variant="subtitle2">
                {getCurrencySymbol(currency)}
                {subtotal.toFixed(2)}
              </Typography>
            </Stack>

            {/* Total de comisiones */}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Comisiones de categorías
              </Typography>
              <Typography variant="subtitle2">
                {getCurrencySymbol(currency)}
                {(visualSubtotal - subtotal).toFixed(2)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Subtotal
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {getCurrencySymbol(currency)}
                {visualSubtotal.toFixed(2)}
              </Typography>
            </Stack>

            {isAndorra && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  IGI (4.5%) sobre total visual
                </Typography>
                <Typography variant="subtitle2">
                  {getCurrencySymbol(currency)}
                  {visualIgi.toFixed(2)}
                </Typography>
              </Stack>
            )}

            <Divider sx={{ my: 1 }} />

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1">Total </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
                  {getCurrencySymbol(currency)}
                  {visualTotal.toFixed(2)}
                </Typography>
                {paymentMethod === PaymentMethodEnum.Pending && (
                  <Typography variant="caption" sx={{ color: 'error.main' }}>
                    Pendiente de pago
                  </Typography>
                )}
              </Box>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Total real (sin comisiones)
              </Typography>
              <Typography variant="body2">
                {getCurrencySymbol(currency)}
                {total.toFixed(2)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Beneficio total
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isTotalProfitPositive ? 'success.main' : 'error.main',
                  fontWeight: 'bold',
                }}
              >
                {getCurrencySymbol(currency)}
                {totalProfit.toFixed(2)}
              </Typography>
            </Stack>
          </Stack>
        </>
      )}
    </Card>
  );
}
